/**
 * Chat input component with send button
 * Handles message composition and submission with auto-growing textarea
 */

import { Send } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useRef, useState } from "react";

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MIN_HEIGHT = 42; // Single line height
const MAX_HEIGHT = 200; // Max height before scrolling

export function ChatInput({ onSend, disabled = false, placeholder = "Type a message..." }: Props) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = `${MIN_HEIGHT}px`;
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${Math.min(scrollHeight, MAX_HEIGHT)}px`;
    }
  };

  const handleChange = (newValue: string) => {
    setValue(newValue);
    // Resize on next frame after DOM update
    requestAnimationFrame(resizeTextarea);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  const submitMessage = () => {
    const text = value.trim();
    if (text && !disabled) {
      onSend(text);
      setValue("");
      // Reset height after clearing
      requestAnimationFrame(resizeTextarea);
    }
  };

  // Submit on Enter, new line on Shift+Enter
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2 border-t p-4 dark:border-gray-700">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        rows={1}
        className="flex-1 resize-none rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        style={{ minHeight: MIN_HEIGHT, maxHeight: MAX_HEIGHT }}
        data-testid="chat-input"
      />
      <button
        type="submit"
        disabled={disabled || !value.trim()}
        className="rounded-lg bg-blue-600 p-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="chat-send-button"
      >
        <Send className="h-5 w-5" />
      </button>
    </form>
  );
}
